import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import type { EntityTimelineTarget } from '@/modules/approvals/entityApprovalTimeline.types';

import { EntityApprovalTimelineModal } from './EntityApprovalTimelineModal';

interface EntityApprovalTimelineContextValue {
  openTimeline: (target: EntityTimelineTarget) => void;
}

const EntityApprovalTimelineContext = createContext<EntityApprovalTimelineContextValue | null>(null);

export function EntityApprovalTimelineProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<EntityTimelineTarget | null>(null);

  const openTimeline = useCallback((next: EntityTimelineTarget) => {
    setTarget(next);
  }, []);

  const closeTimeline = useCallback(() => {
    setTarget(null);
  }, []);

  const value = useMemo(() => ({ openTimeline }), [openTimeline]);

  return (
    <EntityApprovalTimelineContext.Provider value={value}>
      {children}
      <EntityApprovalTimelineModal target={target} onClose={closeTimeline} />
    </EntityApprovalTimelineContext.Provider>
  );
}

export function useEntityApprovalTimeline() {
  const ctx = useContext(EntityApprovalTimelineContext);
  if (!ctx) {
    throw new Error('useEntityApprovalTimeline must be used within EntityApprovalTimelineProvider');
  }
  return ctx;
}

export function useEntityApprovalTimelineOptional() {
  return useContext(EntityApprovalTimelineContext);
}
